import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, Appearance, StripeElementsOptions } from '@stripe/stripe-js';
import * as translator from '../../../../../utils/translator';
import * as http from '../../../../../utils/http';
import PoweredByStripe from '../../../../../components/PoweredByStripe/PoweredByStripe';
import './CarePlusChangePaymentForm.css';

// ---- API contract (SetupIntent-based card change for a Care+ subscription) ----
export const SESSION_ENDPOINT = 'account-update/subscription/care-plus/payment-method/session';
const UPDATE_ENDPOINT = 'account-update/subscription/care-plus/payment-method';
const FINALIZE_ENDPOINT = 'account-update/subscription/care-plus/payment-method/finalize';
export const MIDDLEWARES = [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER];

// One loadStripe promise per publishable key.
const stripePromises: Record<string, Promise<Stripe | null>> = {};
export const getStripe = (publicKey: string): Promise<Stripe | null> => {
  if (!stripePromises[publicKey]) {
    stripePromises[publicKey] = loadStripe(publicKey);
  }
  return stripePromises[publicKey];
};

export const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#269c6e',
    colorText: '#1e2d50',
    colorDanger: '#e74c3c',
    fontFamily: 'Roboto, sans-serif',
    borderRadius: '5px',
  },
};

export interface Session {
  customerSessionSecret: string;
  publicKey: string;
  paymentMethods: string[];
  currency: string;
}

// Desktop opens the card form expanded; mobile keeps every method collapsed so the
// list stays compact and the customer taps the one they want. Evaluated at mount.
const isDesktopViewport = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 765px)').matches;

interface Props {
  subscriptionID: number;
  mode: 'view' | 'recovery';
  language: string;
  onClose: () => void;
  onSaved: () => void;
  setStickyNotificationDetails: (details: any) => void;
}

// ============================================================================
// Inner form — lives inside <Elements>, drives the SetupIntent save + 3DS
// ============================================================================

interface FormProps {
  subscriptionID: number;
  mode: 'view' | 'recovery';
  language: string;
  onClose: () => void;
  onSaved: () => void;
  setStickyNotificationDetails: (details: any) => void;
}

export const ChangePaymentForm: React.FC<FormProps> = ({ subscriptionID, mode, language, onClose, onSaved, setStickyNotificationDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const elementWrapRef = useRef<HTMLDivElement>(null);

  const t = (path: string) => translator.translate(language, 'my_account', path);

  const showError = useCallback((message?: string) => {
    setStickyNotificationDetails({
      show: true,
      level: 'error',
      title: t('CarePlus/paymentMethod/errorTitle'),
      content: message || t('CarePlus/paymentMethod/errorBody'),
    });
  }, [setStickyNotificationDetails, language]);

  const showSuccess = useCallback(() => {
    setStickyNotificationDetails({
      show: true,
      level: 'success',
      title: t('CarePlus/paymentMethod/successTitle'),
      content: mode === 'recovery'
        ? t('CarePlus/paymentMethod/successRecovery')
        : t('CarePlus/paymentMethod/successView'),
    });
  }, [setStickyNotificationDetails, language, mode]);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements || processing) return;
    setProcessing(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showError(submitError.message);
        return;
      }

      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({ elements });
      if (tokenError || !confirmationToken) {
        showError(tokenError?.message);
        return;
      }

      const returnUrl = window.location.href;
      const res: any = await http.postToServer(UPDATE_ENDPOINT, { subscriptionID, confirmationToken: confirmationToken.id, returnUrl }, MIDDLEWARES);
      const data = res?.data ?? res;

      if (data?.status === 'requires_action') {
        const { error: actionError } = await stripe.handleNextAction({ clientSecret: data.clientSecret });
        if (actionError) {
          showError(actionError.message);
          return;
        }
        const finalizeRes: any = await http.postToServer(FINALIZE_ENDPOINT, { subscriptionID, setupIntentId: data.setupIntentId }, MIDDLEWARES);
        const finalizeData = finalizeRes?.data ?? finalizeRes;
        if (finalizeData?.status !== 'ok') {
          showError();
          return;
        }
      } else if (data?.status !== 'ok') {
        showError();
        return;
      }

      showSuccess();
      onSaved();
    } catch (err) {
      showError();
    } finally {
      setProcessing(false);
    }
  }, [stripe, elements, processing, subscriptionID, onSaved, showError, showSuccess]);

  // The PaymentElement mis-measures its iframe height when it mounts inside a freshly
  // portaled modal, so the accordion renders clipped until Stripe recomputes. Stripe
  // recomputes via a ResizeObserver on its OWN element — it reacts to the element's real
  // box size changing, NOT to window 'resize' events. So we briefly shrink the element's
  // wrapper by 1px and revert it: that real size change trips Stripe's observer and it
  // recomputes the correct height. Repeated as fonts/layout settle. (This is exactly what
  // dragging the device dimensions did by hand.)
  const forceStripeRemeasure = useCallback(() => {
    const el = elementWrapRef.current;
    if (!el) return;
    el.style.width = 'calc(100% - 1px)';
    setTimeout(() => {
      if (elementWrapRef.current) elementWrapRef.current.style.width = '';
    }, 50);
  }, []);

  const handleReady = useCallback(() => {
    setReady(true);
    forceStripeRemeasure();
    setTimeout(forceStripeRemeasure, 250);
    setTimeout(forceStripeRemeasure, 600);
  }, [forceStripeRemeasure]);

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
      <div ref={elementWrapRef}>
        <PaymentElement
          options={{ layout: { type: 'accordion', defaultCollapsed: !isDesktopViewport() } }}
          onReady={handleReady}
        />
      </div>

      <div className="cppm-badge">
        <PoweredByStripe />
      </div>

      <div className="cppm-actions">
        <button
          type="button"
          className="cppm-btn cppm-cancel"
          onClick={onClose}
          disabled={processing}
        >
          {t('CarePlus/paymentMethod/cancel')}
        </button>
        <button
          type="submit"
          className="cppm-btn cppm-save"
          disabled={!stripe || !ready || processing}
        >
          {processing ? t('CarePlus/paymentMethod/saving') : t('CarePlus/paymentMethod/save')}
        </button>
      </div>
    </form>
  );
};

// ============================================================================
// Modal — portals a dim backdrop + white card; fetches the CustomerSession
// ============================================================================

const CarePlusChangePaymentForm: React.FC<Props> = ({ subscriptionID, mode, language, onClose, onSaved, setStickyNotificationDetails }) => {
  const [session, setSession] = useState<Session | null>(null);

  const t = (path: string) => translator.translate(language, 'my_account', path);

  // Fetch the CustomerSession for this subscription.
  useEffect(() => {
    let cancelled = false;
    http.postToServer(SESSION_ENDPOINT, { subscriptionID }, MIDDLEWARES)
      .then((res: any) => {
        const data = res?.data ?? res;
        if (!cancelled && data?.customerSessionSecret) {
          setSession({
            customerSessionSecret: data.customerSessionSecret,
            publicKey: data.publicKey,
            paymentMethods: data.paymentMethods ?? [],
            currency: data.currency,
          });
        }
      })
      .catch(() => { /* leaves the spinner up; sticky-notification errors come from save */ });
    return () => { cancelled = true; };
  }, [subscriptionID]);

  // Lock background page scroll while the modal is open.
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Lead with 'card' so it's the first (expanded) accordion item.
  const orderedMethods = session
    ? [...session.paymentMethods].sort((a, b) => (a === 'card' ? -1 : b === 'card' ? 1 : 0))
    : [];

  const options = session ? {
    mode: 'setup',
    currency: session.currency,
    paymentMethodCreation: 'manual',
    paymentMethodTypes: orderedMethods,
    customerSessionClientSecret: session.customerSessionSecret,
    appearance,
    locale: language,
  } as StripeElementsOptions : null;

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  return createPortal(
    <div className="cppm-overlay" onClick={onClose}>
      <div className="cppm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cppm-header">
          <span className="cppm-title">{t('CarePlus/detail/changePaymentMethod')}</span>
          <button type="button" className="cppm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {session && options ? (
          <Elements stripe={getStripe(session.publicKey)} options={options}>
            <ChangePaymentForm
              subscriptionID={subscriptionID}
              mode={mode}
              language={language}
              onClose={onClose}
              onSaved={onSaved}
              setStickyNotificationDetails={setStickyNotificationDetails}
            />
          </Elements>
        ) : (
          <div className="cppm-loading">
            <span className="cppm-spinner" role="progressbar" aria-label={t('CarePlus/paymentMethod/loading')} />
          </div>
        )}
      </div>
    </div>,
    modalRoot
  );
};

export default CarePlusChangePaymentForm;
